/**
 * Local fallback API client. The task brief asks us to import `LoctopusClient` from
 * `@loctopus/sdk`, but at the time this was written `packages/sdk` only contains a README (no
 * `package.json`/`src`, nothing exported) - there is nothing to import yet. This file is the
 * documented stand-in: a thin typed fetch wrapper that talks to the routes listed in
 * docs/API.md's "Client Dashboard API" section. When `@loctopus/sdk` ships a real
 * `LoctopusClient`, this file should be deleted and every `import { api } from './lib/api'` in
 * apps/dashboard swapped for the SDK client - the method names below were chosen to make that
 * swap mechanical.
 *
 * Auth: the Client Dashboard API is "client session + tenant-scoped RBAC" (docs/API.md), not a
 * tenant secret/public API key - so requests go with `credentials: 'include'` (session cookie)
 * rather than an Authorization header. There is no documented login route yet in docs/API.md; the
 * login screen/session bootstrap is out of this agent's scope and presumably lands with
 * packages/auth. `lib/auth.tsx` in this app treats "whoever the session cookie says" as the
 * current ClientUser and fetches it from `GET /dashboard/me` - also not yet in docs/API.md, so
 * that one call is speculative and flagged again in lib/auth.tsx.
 *
 * Route shapes used here follow docs/API.md exactly:
 *  - Server-side management routes (`/v1/events`, `/v1/events/:id/sessions`, etc.) are mirrored
 *    for the dashboard under the same paths, scoped to the authenticated tenant by the session
 *    rather than by API key (docs/API.md: "Mirrors the server-side management routes above,
 *    scoped to the authenticated tenant").
 *  - Dashboard-only routes use the `/dashboard/...` paths listed explicitly in docs/API.md.
 *
 * docs/ARCHITECTURE_V2.md §9/§12 (agent 7, "Client dashboard UI"): repointed from `/v1/...` to
 * `/dashboard/...` and from the old single `apps/api` Worker (:8787) to the new Client Worker
 * `apps/client-api` (:8788, see BASE_URL below) - confirmed route-by-route against
 * `apps/client-api/src/routes/management/*.ts`, which register the exact same
 * `register*Routes(app, basePath)` handlers apps/api did, just mounted with `basePath='/dashboard'`
 * only (that Worker never mounts `/v1` at all - §2's table).
 *
 * `orders.get`/`orders.resend`/`tickets.transfer`/`tickets.resend` below USED to have no
 * `/dashboard` equivalent at all - the real server route lived only in
 * `routes/public/{orders,tickets}.ts` (buyer-facing, `/v1`-only on the Storefront/Public API
 * Workers), reachable from a dashboard session only via the old single-Worker `resolveTenant`'s
 * now-removed `loctopus_client_session` cookie fallback on `/v1/*` - a real regression confirmed
 * live post-split (Order Detail page, "resend ticket", "transfer ticket" all completely broken for
 * a signed-in staff session). Fixed by adding real `/dashboard`-mounted, permission-gated staff
 * equivalents on `apps/client-api` (`routes/management/orders.ts`'s `GET .../orders/:id`/
 * `POST .../orders/:id/resend`, `routes/management/checkIn.ts`'s
 * `POST .../tickets/:id/transfer`) - every call below now goes through `BASE_URL` like every other
 * dashboard call, no more legacy :8787 fallback anywhere in this file.
 */

import type {
  AddOn,
  ApiErrorBody,
  ApiKeySummary,
  BrandingTheme,
  ClientPackage,
  ClientUser,
  CustomQuestion,
  EventSession,
  LoctopusEvent,
  Invoice,
  InvoiceLine,
  NotificationTemplate,
  Order,
  OrderItem,
  PaginatedResponse,
  Payout,
  PromoCode,
  RefundPolicy,
  Role,
  SalesReport,
  SeatMap,
  Ticket,
  TicketType,
  TenantBillingPlan,
  TenantDomain,
  TenantSettings,
  TenantStatus,
  WaitlistEntry,
  WebhookDelivery,
  WebhookEndpoint,
} from './types';
import { getMode } from './mode';
import { getReauthToken } from './reauth';
import { promptForReauth } from './reauthController';

export const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/** Real response shape of `POST /dashboard/orders/:id/resend` (apps/client-api's
 * routes/management/orders.ts) - shared by `orders.resend`/`tickets.resend` below, which are the
 * same server call under two different names. */
interface ResendTicketsResult {
  orderId: string;
  resent: boolean;
  ticketCount: number;
  notificationStatus: 'sent' | 'failed';
  notificationLogId: string;
  notificationError?: string;
}

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Record<string, unknown>;
  // REAL BUG FOUND AND FIXED: this class dropped `body.error.requestId` entirely - every caller
  // that wanted to show the user a request ID to quote back had nothing to read it from, even
  // though the server always sends one (apps/admin's equivalent ApiError already kept it).
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiRequestError';
    this.code = body.error.code;
    this.status = status;
    this.details = body.error.details;
    this.requestId = body.error.requestId;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  idempotencyKey?: string;
}

function buildQuery(query?: RequestOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** Reads the `loctopus_csrf` cookie directly - only ever works same-origin (local dev, where this
 * app and the API share a domain). Kept as a fallback below `readCsrfToken()`. */
function readCsrfCookie(): string | undefined {
  const match = document.cookie.match(/(?:^|;\s*)loctopus_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]!) : undefined;
}

// REAL BUG FOUND AND FIXED: this app is served from a different registrable domain than the API
// (e.g. staff.bobeventsandentertainments.com vs loctopus-client-api.aayushkafle2004.workers.dev)
// - `document.cookie` can NEVER read a cookie that belongs to a different origin, no matter what
// SameSite says (that's a same-origin restriction on script access, unrelated to whether the
// cookie is SENT with a request - which it already correctly is). Confirmed live: a real staff
// login succeeded, but its very first mutating request (ticket check-in) failed with "Missing
// X-CSRF-Token header" because `readCsrfCookie()` above always returned `undefined` here - it was
// reading the wrong origin's cookie jar. `apps/client-api`'s login/TOTP responses now also
// include the token directly in the JSON body (`issueCsrfCookie`'s own doc comment); this stores
// that value same-origin (sessionStorage, so it survives a reload within the tab, not just the
// in-memory lifetime of this module) and prefers it over the cookie read.
const CSRF_TOKEN_STORAGE_KEY = 'loctopus_csrf_token';
// In-memory fallback - some restricted in-app browser contexts (e.g. a webview opened from an
// email app) block or silently no-op sessionStorage. This survives regardless, for as long as
// this page load lasts (storeCsrfToken always sets this FIRST, before even attempting storage).
let inMemoryCsrfToken: string | undefined;

function storeCsrfToken(token: string | undefined): void {
  if (!token) return;
  inMemoryCsrfToken = token;
  try {
    sessionStorage.setItem(CSRF_TOKEN_STORAGE_KEY, token);
  } catch {
    // Private-browsing/storage-blocked - the in-memory copy above still covers this page load;
    // only a hard reload in a storage-blocked context would lose it, which nothing client-side
    // can fix anyway.
  }
}

function readCsrfToken(): string | undefined {
  if (inMemoryCsrfToken) return inMemoryCsrfToken;
  try {
    const stored = sessionStorage.getItem(CSRF_TOKEN_STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // ignore - fall through to cookie read
  }
  return readCsrfCookie();
}

const SAFE_METHODS = new Set(['GET', undefined]);

async function performRequest<T>(path: string, opts: RequestOptions): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  // Single choke point for the Test/Live switch (lib/mode.ts) - every call in this file goes
  // through `request()`, so this is the one place that needs to know which mode is selected. The
  // server still independently enforces mode isolation and every permission exactly as before;
  // this only selects which mode the session reads.
  headers['X-Loctopus-Mode'] = getMode();
  if (!SAFE_METHODS.has(opts.method)) {
    const csrfToken = readCsrfToken();
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    // Sent whenever we have one, on every mutating call - harmless for actions that don't need it
    // (the server only checks this header for the handful of permissions in
    // REAUTH_REQUIRED_PERMISSIONS, apps/api/src/middleware/reauth.ts), and lets a token obtained
    // for one reauth-gated action cover another within its 5-minute window without re-prompting.
    const reauthToken = getReauthToken();
    if (reauthToken) headers['X-Reauth-Token'] = reauthToken;
  }

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}${buildQuery(opts.query)}`, {
      method: opts.method ?? 'GET',
      headers,
      credentials: 'include',
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch (networkErr) {
    // The raw message here (e.g. the browser's own "Failed to fetch") is never shown directly -
    // lib/errorDisplay.ts's toParsedError() special-cases code:'network_error' and renders the
    // shared, plain-language message instead (packages/shared's parseNetworkOrUnknownError).
    throw new ApiRequestError(0, {
      error: {
        code: 'network_error',
        message: networkErr instanceof Error ? networkErr.message : 'Network error.',
      },
    });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  // REAL BUG FOUND AND FIXED: `JSON.parse(text)` used to run unguarded - a non-JSON failure body
  // (a proxy's HTML error page, a truncated response, anything that isn't the documented envelope)
  // would throw a raw SyntaxError instead of a readable ApiRequestError, so the UI had nothing
  // sensible to show.
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    throw new ApiRequestError(res.status, {
      error: {
        code: 'unknown_error',
        message: res.ok
          ? `${path} returned a response that was not valid JSON.`
          : `Request to ${path} failed with HTTP ${res.status} and a response body that was not valid JSON.`,
      },
    });
  }

  if (!res.ok) {
    const body: ApiErrorBody =
      json && typeof json === 'object' && 'error' in json
        ? (json as ApiErrorBody)
        : {
            error: {
              code: 'unknown_error',
              message: `Request to ${path} failed with HTTP ${res.status} and no error body.`,
            },
          };
    throw new ApiRequestError(res.status, body);
  }

  // Any response may carry a fresh `csrfToken` (every login/TOTP/enroll response does - see
  // readCsrfToken()'s doc comment above) - captured generically here, in the one choke point
  // every call already goes through, rather than in each individual auth method.
  if (json && typeof json === 'object' && 'csrfToken' in json) {
    storeCsrfToken((json as { csrfToken?: string }).csrfToken);
  }

  return json as T;
}

/**
 * The shared reauth-retry mechanism (work order: "build this once as a shared helper and use it
 * for every reauth-protected action"). Rather than wiring a reauth check into every individual
 * mutation's onError handler, this is done ONCE here, in the single choke point every dashboard
 * API call already goes through - so every reauth-gated action is covered automatically, not just
 * the ones a developer remembered to add a check to. `details.reauthRequired` (set by
 * apps/api/src/middleware/reauth.ts) is what makes this reliable, rather than string-matching the
 * error message.
 *
 * Only retries ONCE (`alreadyRetried`) - if the server still rejects after a successful reauth
 * (clock skew, a second concurrent reauth-expiry, etc.) this surfaces as a real error rather than
 * looping forever.
 */
async function request<T>(path: string, opts: RequestOptions = {}, alreadyRetried = false): Promise<T> {
  try {
    return await performRequest<T>(path, opts);
  } catch (err) {
    if (
      !alreadyRetried &&
      err instanceof ApiRequestError &&
      err.details?.reauthRequired === true
    ) {
      await promptForReauth(); // resolves once GlobalReauthDialog has stored a fresh token
      return request<T>(path, opts, true);
    }
    throw err;
  }
}

function idem(): string {
  return crypto.randomUUID();
}

export interface DashboardLoginResult {
  totpRequired: boolean;
  pendingToken?: string;
  /** Set instead of `pendingToken` when this account has no TOTP enrolled yet AND the tenant has
   * forced staff 2FA (tenant_settings.staff2faForced, docs/WALKTHROUGH.md "2FA" point 5). */
  enrollmentRequired?: boolean;
  enrollmentPendingToken?: string;
}

export interface TotpEnrollmentStart {
  qrSvg: string;
  manualEntryKey: string;
  accountLabel: string;
}

export const api = {
  me: {
    // `tenantStatus` is ASSUMED (see lib/types.ts's `TenantStatus` doc comment) - every other
    // field here was already real/confirmed before this change.
    get: () =>
      request<
        ClientUser & {
          tenantId: string;
          tenantName?: string;
          permissions: string[];
          tenantStatus?: TenantStatus;
        }
      >('/dashboard/me'),
  },

  /** docs/ARCHITECTURE_V2.md §3.4/§6/§12 agent 7: module-aware UI. Backs `lib/clientPackage.tsx`'s
   * `useClientPackage()`/`hasModule()` - a courtesy-only read, same "UI hides what the server
   * already blocks, never the reverse" rule as `RequirePermission` (`lib/auth.tsx`). */
  clientPackage: {
    get: () => request<ClientPackage>('/dashboard/client-package'),
  },

  auth: {
    login: (email: string, password: string, tenantSlug: string) =>
      request<DashboardLoginResult>('/dashboard/auth/login', {
        method: 'POST',
        body: { email, password, tenantSlug },
      }),
    loginTotp: (pendingToken: string, code: string) =>
      request<DashboardLoginResult>('/dashboard/auth/login/totp', {
        method: 'POST',
        body: { pendingToken, code },
      }),
    logout: () => request<{ ok: boolean }>('/dashboard/auth/logout', { method: 'POST' }),
    reauth: (input: { password?: string; totpCode?: string }) =>
      request<{ reauthToken: string }>('/dashboard/auth/reauth', {
        method: 'POST',
        body: input,
      }),
    /** Forced-at-login mandatory enrollment (tenant has staff2faForced on) - pre-session, gated
     * by the enrollmentPendingToken DashboardLoginResult just returned. */
    enrollStart: (enrollmentPendingToken: string) =>
      request<TotpEnrollmentStart>('/dashboard/auth/enroll/start', {
        method: 'POST',
        body: { enrollmentPendingToken },
      }),
    enrollConfirm: (enrollmentPendingToken: string, code: string) =>
      request<DashboardLoginResult & { recoveryCodes: string[] }>('/dashboard/auth/enroll/confirm', {
        method: 'POST',
        body: { enrollmentPendingToken, code },
      }),
    /** Self-service: both turning 2FA ON for the first time (optional by default, see
     * docs/WALKTHROUGH.md "2FA" point 5) and resetting an already-enrolled secret are the exact
     * same server operation - `request()`'s reactive reauth retry handles the prompt
     * automatically, no reauthToken needed from the caller. */
    totpResetStart: () =>
      request<TotpEnrollmentStart>('/dashboard/auth/totp/reset', { method: 'POST' }),
    totpResetConfirm: (code: string) =>
      request<{ recoveryCodes: string[] }>('/dashboard/auth/totp/reset/confirm', {
        method: 'POST',
        body: { code },
      }),
    /** Always resolves with the same generic `{ok: true}` shape regardless of whether the email
     * matched a real account - apps/client-api's route never reveals account existence (same
     * "deliberately vague" rule `/dashboard/auth/login` already follows for bad credentials). */
    forgotPassword: (email: string, tenantSlug: string) =>
      request<{ ok: true }>('/dashboard/auth/forgot-password', {
        method: 'POST',
        body: { email, tenantSlug },
      }),
    resetPassword: (token: string, password: string) =>
      request<{ ok: true }>('/dashboard/auth/reset-password', {
        method: 'POST',
        body: { token, password },
      }),
  },

  events: {
    list: (query?: { cursor?: string; limit?: number; status?: string }) =>
      request<PaginatedResponse<LoctopusEvent>>('/dashboard/events', { query }),
    get: (id: string) => request<LoctopusEvent>(`/dashboard/events/${id}`),
    create: (body: Partial<LoctopusEvent>) =>
      request<LoctopusEvent>('/dashboard/events', { method: 'POST', body, idempotencyKey: idem() }),
    update: (id: string, body: Partial<LoctopusEvent>) =>
      request<LoctopusEvent>(`/dashboard/events/${id}`, { method: 'PATCH', body }),
  },

  sessions: {
    list: (eventId: string, query?: { cursor?: string; limit?: number }) =>
      request<PaginatedResponse<EventSession>>(`/dashboard/events/${eventId}/sessions`, { query }),
    create: (eventId: string, body: Partial<EventSession>) =>
      request<EventSession>(`/dashboard/events/${eventId}/sessions`, {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
    update: (id: string, body: Partial<EventSession>) =>
      request<EventSession>(`/dashboard/sessions/${id}`, { method: 'PATCH', body }),
  },

  ticketTypes: {
    list: (eventId: string, query?: { cursor?: string; limit?: number }) =>
      request<PaginatedResponse<TicketType>>(`/dashboard/events/${eventId}/ticket-types`, { query }),
    create: (eventId: string, body: Partial<TicketType>) =>
      request<TicketType>(`/dashboard/events/${eventId}/ticket-types`, {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
    update: (id: string, body: Partial<TicketType>) =>
      request<TicketType>(`/dashboard/ticket-types/${id}`, { method: 'PATCH', body }),
  },

  seatMaps: {
    list: (eventId: string) =>
      request<PaginatedResponse<SeatMap>>(`/dashboard/events/${eventId}/seat-maps`),
    // REAL BUG FOUND AND FIXED (apps/dashboard/src/features/seatmap/SeatMapEditorPage.tsx): this
    // used to type its body as `Partial<SeatMap>` (the RESPONSE row shape - id/tenantId/eventId/
    // name/layout/createdAt) and expect a `SeatMap` back. The real route
    // (apps/client-api/src/routes/management/catalog.ts, importSeatMapSchema) is a full atomic
    // import - it requires a top-level `sections` array (name/priceZone/seats[].rowLabel/
    // seatNumber), which this method's old signature had no way to even express, and returns
    // `{seatMapId}`, not a full SeatMap row. Every real "Save seat map" click has always 400'd on
    // a missing `sections` field. Request/response types now match the real wire contract exactly.
    create: (
      eventId: string,
      body: {
        name: string;
        layout?: Record<string, unknown>;
        sections: Array<{
          name: string;
          priceZone: string;
          isAccessible?: boolean;
          seats: Array<{ rowLabel: string; seatNumber: string; x?: number; y?: number }>;
        }>;
      },
    ) =>
      request<{ seatMapId: string }>(`/dashboard/events/${eventId}/seat-maps`, {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
    // REAL GAP FOUND AND FIXED: `apps/client-api/src/routes/management/catalog.ts` now has a real
    // `PATCH /dashboard/seat-maps/:id` (flat - not nested under `:eventId`, same shape as the
    // sibling promo-codes/add-ons/ticket-types/custom-questions PATCH routes in that file).
    // Edit-only: name/layout (its own top-level fields), never sections/seats - see that route's
    // doc comment. Returns `{id, eventId, name?, layout?}`, not a full `SeatMap` row (no `tenantId`/
    // `createdAt` round-trip needed by any caller of this method).
    update: (seatMapId: string, body: { name?: string; layout?: Record<string, unknown> }) =>
      request<{ id: string; eventId: string; name?: string; layout?: Record<string, unknown> }>(
        `/dashboard/seat-maps/${seatMapId}`,
        { method: 'PATCH', body },
      ),
    /**
     * `docs/API.md` says the seat-maps route is "CRUD + seat import" but does not give the import
     * payload shape. We POST the raw import document (see lib/seatmap-shape.ts) to this path and
     * expect the Ticketing-core agent's `importSeatMapFromJson` to be wired up behind it. FLAG,
     * RE-CONFIRMED against apps/client-api: there is no separate `/seat-maps/import` sub-route
     * there either - the real `importSeatMapFromJson` call is wired up behind plain
     * `POST .../seat-maps` (the same path `create` above already uses), not `.../seat-maps/import`.
     * This method's target path still doesn't exist under either prefix - repointed for
     * consistency, not fixed (same pre-existing gap, not introduced by this repoint).
     */
    import: (eventId: string, body: unknown) =>
      request<SeatMap>(`/dashboard/events/${eventId}/seat-maps/import`, {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
  },

  promoCodes: {
    list: (eventId: string) =>
      request<PaginatedResponse<PromoCode>>(`/dashboard/events/${eventId}/promo-codes`),
    create: (eventId: string, body: Partial<PromoCode>) =>
      request<PromoCode>(`/dashboard/events/${eventId}/promo-codes`, {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
    update: (id: string, body: Partial<PromoCode>) =>
      request<PromoCode>(`/dashboard/promo-codes/${id}`, { method: 'PATCH', body }),
  },

  addOns: {
    list: (eventId: string) =>
      request<PaginatedResponse<AddOn>>(`/dashboard/events/${eventId}/add-ons`),
    create: (eventId: string, body: Partial<AddOn>) =>
      request<AddOn>(`/dashboard/events/${eventId}/add-ons`, {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
    update: (id: string, body: Partial<AddOn>) =>
      request<AddOn>(`/dashboard/add-ons/${id}`, { method: 'PATCH', body }),
  },

  customQuestions: {
    list: (eventId: string) =>
      request<PaginatedResponse<CustomQuestion>>(`/dashboard/events/${eventId}/custom-questions`),
    create: (eventId: string, body: Partial<CustomQuestion>) =>
      request<CustomQuestion>(`/dashboard/events/${eventId}/custom-questions`, {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
    update: (id: string, body: Partial<CustomQuestion>) =>
      request<CustomQuestion>(`/dashboard/custom-questions/${id}`, { method: 'PATCH', body }),
  },

  waitlist: {
    list: (query?: { eventId?: string; status?: string; cursor?: string; limit?: number }) =>
      request<PaginatedResponse<WaitlistEntry>>('/dashboard/waitlist', { query }),
    create: (body: Partial<WaitlistEntry>) =>
      request<WaitlistEntry>('/dashboard/waitlist', {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
  },

  orders: {
    list: (query?: {
      cursor?: string;
      limit?: number;
      status?: string;
      eventId?: string;
      q?: string;
    }) => request<PaginatedResponse<Order>>('/dashboard/orders', { query }),
    // REAL GAP FOUND AND FIXED: this used to have no `/dashboard` equivalent at all and called the
    // OLD `apps/api` Worker directly (see this file's top comment) - now calls the real staff
    // route added on apps/client-api (`routes/management/orders.ts`'s `GET /dashboard/orders/:id`,
    // gated `orders.view`). That route returns `{order, items, tickets}` - the order's own fields
    // NESTED under an `order` key, not flattened, and (unlike the old buyer-facing route this
    // replaces) real tickets for the order too - unwrapped here into the flat shape
    // OrderDetailPage.tsx already expects.
    get: (id: string) =>
      request<{ order: Order; items: OrderItem[]; tickets: Ticket[]; promoCode: string | null }>(
        `/dashboard/orders/${id}`,
      ).then(
        (r): Order & { items: OrderItem[]; tickets: Ticket[] } => ({
          ...r.order,
          promoCode: r.promoCode,
          items: r.items,
          tickets: r.tickets,
        }),
      ),
    refund: (id: string, body: { amountMinor?: number; reason?: string }) =>
      request<Order>(`/dashboard/orders/${id}/refund`, {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
    // REAL GAP FOUND AND FIXED: same as `get` above - now calls the real staff route
    // (`POST /dashboard/orders/:id/resend`, gated `tickets.resend`), which sends the ticket
    // delivery email right away (not a 15-minute-delayed schedule, see that route's own doc
    // comment) and reports back whether it genuinely sent.
    resend: (id: string) =>
      request<ResendTicketsResult>(`/dashboard/orders/${id}/resend`, {
        method: 'POST',
        idempotencyKey: idem(),
      }),
    cancelEvent: (eventId: string, body: { reason: string }) =>
      request<{ ok: true; refundedOrders: number }>(`/dashboard/events/${eventId}/cancel`, {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
  },

  tickets: {
    // REAL GAP FOUND AND FIXED: same as `orders.get` above - now calls the real staff route added
    // on apps/client-api (`routes/management/checkIn.ts`'s `POST /dashboard/tickets/:id/transfer`,
    // gated `tickets.transfer`, Manager-only per packages/rbac/src/presets.ts). The server's body
    // shape is `{newHolderName, newHolderEmail}` (same as the buyer-facing route it replaces) -
    // mapped from this method's own `{holderName, holderEmail}` param names so no caller needs to
    // change. Response is `{ticketId, newTicketId, status}`, not a full `Ticket` - the old typing
    // here was never actually checked against the real server response.
    transfer: (id: string, body: { holderName: string; holderEmail: string }) =>
      request<{ ticketId: string; newTicketId: string; status: 'transferred' }>(
        `/dashboard/tickets/${id}/transfer`,
        {
          method: 'POST',
          body: { newHolderName: body.holderName, newHolderEmail: body.holderEmail },
          idempotencyKey: idem(),
        },
      ),
    // REAL GAP FOUND AND FIXED: identical gap/fix to `orders.resend` above (this method just
    // exposes the same server endpoint under the ticket-transfer page's own naming).
    resend: (orderId: string) =>
      request<ResendTicketsResult>(`/dashboard/orders/${orderId}/resend`, {
        method: 'POST',
        idempotencyKey: idem(),
      }),
    // Owner's explicit ask: scanning must live INSIDE this app (same login, same domain,
    // staff.bobeventsandentertainments.com) rather than requiring a second separate app/URL - see
    // apps/scanner's identically-shaped calls (lib/api.ts) for the original, already-proven-live
    // version of these same two routes this is ported from.
    get: (id: string) =>
      request<{
        ticket: {
          id: string;
          status: 'issued' | 'checked_in' | 'transferred' | 'refunded' | 'cancelled' | 'void';
          holderName: string | null;
          ticketTypeName: string | null;
          sessionId: string;
          checkedInAt?: string | null;
        };
      }>(`/dashboard/tickets/${encodeURIComponent(id)}`),
    // REAL GAP FOUND AND FIXED: `qrPayload` is the one field the server actually uses for
    // anything (`checkInSchema` - `sessionId`/`deviceId`/`scannedAt` are silently accepted and
    // ignored, a separate pre-existing gap documented in apps/scanner's own history, not touched
    // here). Without it, the server's cryptographic QR-signature check
    // (`routes/management/checkIn.ts`'s `verifyTicketQr`) never ran at all - anyone who merely
    // knew or guessed a real ticket id could check it in without ever having scanned a genuine
    // signed QR code. Optional because a manual-entry/override path (no camera scan at all) has
    // no signature to send - see that same route's comment on why that's a deliberate, separate,
    // already-permissioned path.
    checkIn: (id: string, body: { sessionId: string; deviceId: string; scannedAt: string; qrPayload?: string }) =>
      request<
        | { ticketId: string; result: 'granted'; status: string; checkedInBy: string }
        | { ticketId: string; result: 'not_event_day'; status: string; eventDate: string; daysUntilEvent: number }
      >(`/dashboard/tickets/${encodeURIComponent(id)}/check-in`, {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
  },

  reports: {
    // REAL GAP FOUND AND FIXED: the server route now does real week/month bucketing and computes
    // real `ticketsSold`/`netMinor` (apps/client-api/src/routes/management/reports.ts) - the
    // fake-filling adapter this used to need (`ticketsSold: 0`, `netMinor: grossMinor`, and
    // `granularity` only ever widening the trailing window instead of actually changing bucket
    // size) is gone; the server response now matches this app's `SalesReport` type exactly.
    // `eventId`/`from`/`to` are still unsupported server-side (not part of this task's scope) -
    // silently ignored here rather than sent and ignored server-side too, so a caller passing them
    // isn't misled into thinking they filtered anything.
    sales: (query: { eventId?: string; from?: string; to?: string; granularity?: 'day' | 'week' | 'month' }) => {
      const days = query.granularity === 'month' ? 180 : query.granularity === 'week' ? 90 : 30;
      return request<SalesReport>('/dashboard/reports/sales', {
        query: { days, granularity: query.granularity ?? 'day' },
      });
    },
  },

  staff: {
    // REAL GAP FOUND AND FIXED: `GET /dashboard/staff` was a stub (always `data: []`) - it now
    // lists every real `client_users` row for this tenant with role names, invite status, and
    // last login (apps/client-api/src/routes/dashboard/extras.ts).
    list: () => request<PaginatedResponse<ClientUser>>('/dashboard/staff'),
    invite: (body: { email: string; roleIds: string[] }) =>
      request<ClientUser>('/dashboard/staff', { method: 'POST', body, idempotencyKey: idem() }),
    updateRole: (id: string, body: { roleIds: string[] }) =>
      request<{ id: string; roleNames: string[] }>(`/dashboard/staff/${id}/role`, {
        method: 'PATCH',
        body,
      }),
    setStatus: (id: string, status: 'active' | 'disabled') =>
      request<{ id: string; status: 'active' | 'disabled' }>(`/dashboard/staff/${id}/status`, {
        method: 'PATCH',
        body: { status },
      }),
    /** Manager-initiated 2FA reset for ONE specific staff member (never the acting manager's own
     * account - that is `api.auth.totpResetStart/Confirm` on the Security page instead). The
     * target never confirms anything here; their own next login naturally lands on the
     * mandatory-enrollment screen since the server flips `totpEnabled` back to `false`. */
    resetTotp: (id: string) =>
      request<{ id: string; totpEnabled: boolean; message: string }>(
        `/dashboard/staff/${id}/totp/reset`,
        { method: 'POST' },
      ),
  },

  roles: {
    list: () => request<PaginatedResponse<Role>>('/dashboard/roles'),
    create: (body: { name: string; permissions: string[] }) =>
      request<Role>('/dashboard/roles', { method: 'POST', body, idempotencyKey: idem() }),
  },

  paymentAccounts: {
    // REAL BUGS FOUND AND FIXED (apps/dashboard/src/features/settings/PaymentOnboardingPage.tsx):
    // (1) the server's onboardSchema (apps/api/src/routes/dashboard/extras.ts) requires
    // `returnUrl`/`refreshUrl` - this call never sent either, so every real click 400'd before
    // Stripe was even reached; (2) the server returns `{onboardingUrl, connectedAccountId}`, not
    // `{url}` - even a successful call could never have redirected the browser anywhere. `mode`
    // was also a body field here, which the schema doesn't even declare - mode for a `/dashboard/
    // *` session request comes from the `X-Loctopus-Mode` header `request()` already sends on
    // every call (lib/mode.ts's global Test/Live switch), so it's dropped as a body field here.
    onboard: (body: { provider: 'stripe' | 'paypal'; returnUrl: string; refreshUrl: string }) =>
      request<{ onboardingUrl: string; connectedAccountId: string }>(
        '/dashboard/payment-accounts/onboard',
        { method: 'POST', body, idempotencyKey: idem() },
      ),
  },

  payouts: {
    list: (query?: { cursor?: string; limit?: number; status?: string }) =>
      request<PaginatedResponse<Payout>>('/dashboard/payouts', { query }),
  },

  invoices: {
    list: (query?: { cursor?: string; limit?: number; status?: string }) =>
      request<PaginatedResponse<Invoice>>('/dashboard/invoices', { query }),
    get: (id: string) => request<Invoice & { lines: InvoiceLine[] }>(`/dashboard/invoices/${id}`),
  },

  branding: {
    // Server wraps this in `{ branding: ... | null }` (null for a tenant that has never saved
    // branding yet) - this used to be typed as a bare `BrandingTheme`, so BrandingPage.tsx's
    // `setTheme(brandingQuery.data)` replaced the whole theme with an object that had no
    // `.colors` at all, crashing on `theme.colors.primary` on every render (real bug, found via a
    // headless click-through of every dashboard tab).
    get: () =>
      request<{ branding: BrandingTheme | null }>('/dashboard/branding').then((r) => r.branding),
    update: (body: Partial<BrandingTheme>) =>
      request<{ branding: Partial<BrandingTheme> }>('/dashboard/branding', {
        method: 'PATCH',
        body: { branding: body },
      }).then((r) => r.branding),
  },

  domains: {
    get: () => request<PaginatedResponse<TenantDomain>>('/dashboard/domains'),
    update: (body: { domain: string; type: 'custom' }) =>
      request<TenantDomain>('/dashboard/domains', { method: 'PATCH', body }),
  },

  /**
   * ASSUMED route (dashboard brief task 1, "hide the Test/Live switch") - no `/dashboard/settings`
   * route exists in docs/API.md or anywhere under apps/client-api/apps/api today (confirmed by
   * grepping both); this follows the exact same `GET`/`PATCH` pair style as `branding`/`domains`
   * above, which ARE real documented routes for the same `tenant_settings`-backed-data shape. See
   * lib/types.ts's `TenantSettings` doc comment for the field-level assumption (`hideModeSwitch`).
   */
  settings: {
    get: () => request<TenantSettings>('/dashboard/settings'),
    update: (body: Partial<TenantSettings>) =>
      request<TenantSettings>('/dashboard/settings', { method: 'PATCH', body }),
  },

  /**
   * RECONCILED mid-task, not purely assumed - `packages/db/src/schema/tenants.ts`'s own doc
   * comment on its (now real-shaped) `RefundPolicy` type says this is "Read/written today only by
   * apps/client-api's tenant-Owner route (GET/PATCH /dashboard/refund-policy)" (found uncommitted
   * in this working tree while this change was in progress - see lib/types.ts's `RefundPolicy`
   * doc comment). The route handler itself did not exist yet under
   * apps/client-api/src/routes at the time this was written (confirmed by grepping that
   * directory), so the PATH and BODY SHAPE are reconciled against the backend agent's own schema
   * work, but whether the route is actually live is not - still flagged for a real click-test
   * once it lands.
   */
  refundPolicy: {
    get: () => request<RefundPolicy>('/dashboard/refund-policy'),
    update: (body: RefundPolicy) =>
      request<RefundPolicy>('/dashboard/refund-policy', { method: 'PATCH', body }),
  },

  /**
   * ASSUMED route (dashboard brief task 6, "Billing page") - no `/dashboard/billing-plan` route
   * exists anywhere in this repo today (confirmed by grepping docs/API.md and every Worker's
   * routes directory); read-only, same GET-only style as `payouts.list`/`invoices.list` above
   * (the tenant's own plan assignment is set by a platform admin, never editable from here - see
   * `packages/db/src/schema/billing.ts`'s `planAssignments` table, which has no tenant-writable
   * path at all). See lib/types.ts's `TenantBillingPlan` doc comment for the field-level
   * assumptions (`components[].feePayer`, `issuesTaxInvoices`).
   */
  billingPlan: {
    get: () => request<TenantBillingPlan>('/dashboard/billing-plan'),
  },

  templates: {
    list: () => request<PaginatedResponse<NotificationTemplate>>('/dashboard/templates'),
    update: (id: string, body: Partial<NotificationTemplate>) =>
      request<NotificationTemplate>(`/dashboard/templates/${id}`, { method: 'PATCH', body }),
  },

  apiKeys: {
    list: () => request<PaginatedResponse<ApiKeySummary>>('/dashboard/api-keys'),
    create: (body: {
      name: string;
      type: 'secret' | 'public';
      mode: 'test' | 'live';
      scopes: string[];
    }) =>
      request<ApiKeySummary & { rawKey: string }>('/dashboard/api-keys', {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
    // REAL BUG FOUND AND FIXED (while verifying this repoint against
    // apps/client-api/src/routes/management/webhooksAndKeys.ts, byte-identical to apps/api's copy
    // here): this called `PATCH /v1/api-keys/:id` with body `{revoked: true}`, expecting a full
    // `ApiKeySummary` back. The real route has never been that - it is
    // `POST .../api-keys/:id/revoke`, no body, returning `{id, revoked: true}`. Every real
    // "Revoke" click has always 404'd (PATCH was never a registered method on `/api-keys/:id` at
    // all) - not something this repoint caused, but caught while confirming the path exists under
    // `/dashboard`, so fixed here rather than silently carrying the same dead call forward.
    // ApiKeysPage.tsx only uses this to trigger a query-invalidate/refetch, never the resolved
    // value, so the narrower return type is a safe match.
    revoke: (id: string) =>
      request<{ id: string; revoked: true }>(`/dashboard/api-keys/${id}/revoke`, {
        method: 'POST',
      }),
  },

  webhookEndpoints: {
    list: () => request<PaginatedResponse<WebhookEndpoint>>('/dashboard/webhook-endpoints'),
    create: (body: { url: string; events: string[] }) =>
      request<WebhookEndpoint & { secret: string }>('/dashboard/webhook-endpoints', {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
  },

  webhookDeliveries: {
    list: (query?: { endpointId?: string; status?: string; cursor?: string; limit?: number }) =>
      request<PaginatedResponse<WebhookDelivery>>('/dashboard/webhook-deliveries', { query }),
  },
};
